import LiveDocumentCard from './LiveDocumentCard.js';
import { previewOwnershipRecord } from './documentPreviewState.js';
import { previewOwnsReceipt, readablePreviewText } from './documentPreviewRendering.js';
import UserMessage from "./UserMessage.js?v=45.0.5"
import BotMessage from "./BotMessage.js?v=45.0.13"
import SystemMessage from "./SystemMessage.js?v=45.0.0"
import ToolReceipt from "./ToolReceipt.js?v=1.0.18"
import {
    collapseSupersededPreflightReceipts,
    userFacingToolReceipt,
} from "./toolReceiptVisibility.js?v=1.0.4"
import { artifactProjectionReceiptKey } from "./chatFrontendGuards.js?v=1.0.2"

export default {
    props: {
        session: Object,
        documentPreviews: { type: Array, default: () => [] },
        documentPreviewOwnership: { type: Array, default: () => [] },
        replying: {
            type: Boolean,
            default: false
        },
        collaborative: {
            type: Boolean,
            default: false
        },
        showPopup: {
            type: Boolean,
            required: true
        },
        popupContent: {
            type: String,
            required: true
        },
        activeReview: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            count: 5,
            artifactProjectionStates: {},
            previewReadyArtifacts: {},
            previewUnavailableArtifacts: {},
            previewOwners: {}
        }
    },
    computed: {
        lastUserIndex() {
            if (!this.session || !this.session.data || this.session.data.length === 0) return -1;
            for (let i = this.session.data.length - 1; i >= 0; i--) {
                if (this.session.data[i].sender === 'user') return i;
            }
            return -1;
        },
        loadedMessageIds() {
            const ids = new Set();
            const messages = this.session && Array.isArray(this.session.data)
                ? this.session.data
                : [];
            messages.forEach((message) => {
                const id = this.messageReferenceId(message);
                if (id) ids.add(id);
            });
            return ids;
        },
        visibleToolReceipts() {
            const receipts = this.session && Array.isArray(this.session.tool_receipts)
                ? this.session.tool_receipts
                : [];
            return collapseSupersededPreflightReceipts(receipts, this.activeToolReplyAnchors)
                .map(userFacingToolReceipt)
                .filter(receipt => !this.isPendingSourceReadReceipt(receipt))
                .filter(receipt => !this.isEmptySourceReadReceipt(receipt));
        },
        activeToolReplyAnchors() {
            const messages = this.session && Array.isArray(this.session.data)
                ? this.session.data
                : [];
            const responseMessageIds = new Set();
            const turnIds = new Set();
            const triggerMessageIds = new Set();
            messages.forEach((message, index) => {
                if (!message || message.sender !== 'bot' || message.isLoading !== true) return;
                [message.message_id, message.response_message_id].forEach((value) => {
                    const id = String(value || '').trim();
                    if (id) responseMessageIds.add(id);
                });
                const turnId = String(message.turn_id || '').trim();
                if (turnId) turnIds.add(turnId);
                [message.trigger_message_id, message.ai_response_anchor_message_id].forEach((value) => {
                    const id = String(value || '').trim();
                    if (id) triggerMessageIds.add(id);
                });
                // The placeholder may render before the acceptance response
                // supplies durable IDs. Its nearest preceding user message is
                // still the exact visual anchor for this in-flight answer.
                for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
                    const candidate = messages[cursor];
                    if (!candidate || candidate.sender !== 'user') continue;
                    const triggerId = this.messageReferenceId(candidate);
                    if (triggerId) triggerMessageIds.add(triggerId);
                    break;
                }
            });
            return {
                responseMessageIds: Array.from(responseMessageIds),
                turnIds: Array.from(turnIds),
                triggerMessageIds: Array.from(triggerMessageIds),
            };
        },
        standaloneReceipts() {
            const receipts = this.visibleToolReceipts;
            const messages = this.session && Array.isArray(this.session.data)
                ? this.session.data
                : [];
            const attachedIds = new Set();
            messages.forEach((message) => {
                if (!message) return;
                this.receiptsForMessage(message).forEach((receipt) => {
                    if (receipt && receipt.tool_run_id) attachedIds.add(String(receipt.tool_run_id));
                });
            });
            return receipts.filter(receipt => (
                receipt && receipt.tool_run_id
                // Source reads are part of an assistant response's research
                // timeline. If an edit removes that response, its durable
                // receipt must not fall back to a standalone result card.
                && receipt.tool_name !== 'leer_fuente'
                && !attachedIds.has(String(receipt.tool_run_id))
                // An artifact belongs with the assistant response, even when that
                // response is outside the current pagination window. Rendering it
                // at the conversation bottom would make it look newly generated.
                // A terminal receipt whose response is known to be unavailable is
                // the exception: it is the only durable route back to a mutation
                // that committed just before its generation was superseded.
                && (!this.isArtifactReceiptWithResponse(receipt)
                    || this.isOrphanedArtifactReceipt(receipt))
                && !this.isPendingArtifactReceipt(receipt)
                // Reviews are anchored below their assistant response's research
                // panel. Keep them out of the standalone tail while that response
                // is loading or outside the current pagination window.
                && (!this.isDocumentReviewReceipt(receipt)
                    || this.isOrphanedReviewReceipt(receipt)
                    || this.isUnhostedTerminalReviewReceipt(receipt))
                // Newer receipt payloads explicitly tell us that the trigger is
                // persisted. When it is not in this page, retain its position until
                // that page is loaded instead of relocating it to the bottom.
                && (!this.isReceiptAttachedToUnloadedMessage(receipt)
                    || this.isOrphanedArtifactReceipt(receipt)
                    || this.isOrphanedReviewReceipt(receipt)
                    || this.isUnhostedTerminalReviewReceipt(receipt))
            ));
        }
    },
    components: {
        UserMessage,
        BotMessage,
        SystemMessage,
        ToolReceipt,
        LiveDocumentCard
    },
    methods: {
        hasLiveDocumentPreviewForMessage(message) {
            return this.previewsForMessage(message).some(preview => (
                ['draft', 'edit'].includes(preview.mode)
                && !preview.committed
                && ['preparing', 'drafting', 'editing', 'checking', 'saving', 'stopping'].includes(preview.stage)
                && readablePreviewText(preview.content || preview.base_content || '').trim()
            ));
        },
        hasDocumentOutputForMessage(message) {
            if (!message || message.sender !== 'bot') return false;
            return this.previewsForMessage(message).length > 0
                || this.visibleToolReceipts.some(receipt => (
                    receipt && receipt.tool_name === 'crear_editar_archivo'
                    && this.assistantHostForReceipt(receipt) === message
                ));
        },
        previewsForMessage(message) {
            if (!message || message.sender !== 'bot') return [];
            const previews = this.documentPreviews.filter(preview => this.previewMatchesMessage(preview, message));
            if (!previews.length) return previews;
            const receipts = Array.isArray(this.session?.tool_receipts) ? this.session.tool_receipts : [];
            const retainedReceipts = collapseSupersededPreflightReceipts(receipts);
            return previews.filter(preview => {
                // Preflight failures have no document to inspect. Their receipt
                // and assistant explanation already describe the outcome.
                if (preview.committed || !['incomplete', 'cancelled', 'failed', 'superseded'].includes(preview.stage)) return true;
                if (!readablePreviewText(preview.content || preview.base_content || '').trim()) return false;
                // Older previews predate retry adoption. Hide an anchor-rejected
                // draft only when its receipt was superseded and the same reply
                // saved a replacement with the exact document title.
                const failedReceipt = receipts.find(receipt => previewOwnsReceipt(preview, receipt)
                    && receipt.status === 'failed' && receipt.error?.code === 'artifact_source_anchor_invalid');
                const title = String(preview.title || '').trim();
                const response = String(preview.response_id || preview.response_message_id || '');
                if (preview.mode !== 'draft' || !title || !response || !failedReceipt || retainedReceipts.includes(failedReceipt)) return true;
                return !previews.some(candidate => candidate !== preview && candidate.mode === 'draft'
                    && candidate.committed?.artifact_id && Number(candidate.committed.version) > 0
                    && String(candidate.response_id || candidate.response_message_id || '') === response
                    && String(candidate.title || '').trim() === title);
            });
        },
        previewMatchesMessage(preview, message) {
            if (!message || message.sender !== 'bot') return false;
            if (preview.chat_uuid && String(preview.chat_uuid) !== String(this.session?.uuid || '')) return false;
            const ids = [message.message_id, message.response_message_id, message.client_message_id].filter(Boolean).map(String);
            const response = String(preview.response_id || preview.response_message_id || '');
            if (ids.includes(response)) return true;
            if (!response && preview.turn_id && message.turn_id) return String(preview.turn_id) === String(message.turn_id);
            // Realtime can identify the active run before HTTP acceptance.
            // Historical previews, including unloaded pages, cannot adopt a
            // new placeholder merely because it is the only pending reply.
            if (message.isLoading !== true || message._is_optimistic !== true
                || message.turn_id || message.response_message_id
                || !message.run_id || String(preview.run_id || '') !== String(message.run_id)) return false;
            const messages = Array.isArray(this.session?.data) ? this.session.data : [];
            return messages.filter(item => item.sender === 'bot' && item.isLoading).length === 1
                && !messages.some(item => item !== message && item.sender === 'bot' && response
                    && [item.message_id, item.response_message_id, item.client_message_id].filter(Boolean).map(String).includes(response));
        },
        receiptForPreview(preview) {
            const receipts = this.visibleToolReceipts.filter(receipt => previewOwnsReceipt(preview, receipt));
            return receipts.find(receipt => String(receipt.tool_run_id) === String(preview.tool_run_id))
                || receipts.find(receipt => ['completed', 'partial'].includes(receipt.status)) || receipts[0] || null;
        },
        previewCommittedReady({ preview, committed, projection }) {
            const identity = committed || preview.committed;
            if (!identity) return;
            const handle = `A${identity.artifact_id}:v${identity.version}`;
            this.previewOwners = { ...this.previewOwners, [preview.preview_id]: previewOwnershipRecord(preview) };
            this.previewReadyArtifacts = { ...this.previewReadyArtifacts, [preview.preview_id]: handle };
        },
        previewCommittedUnavailable({ preview, committed }) {
            const identity = committed || preview.committed;
            if (!identity) return;
            const handle = `A${identity.artifact_id}:v${identity.version}`;
            this.previewOwners = { ...this.previewOwners, [preview.preview_id]: previewOwnershipRecord(preview) };
            const previous = this.previewUnavailableArtifacts[preview.preview_id] || [];
            this.previewUnavailableArtifacts = {
                ...this.previewUnavailableArtifacts,
                [preview.preview_id]: [...new Set([...previous, handle])],
            };
        },
        readyArtifactHandles(message) {
            // An authoritative denial also owns its exact compatibility block;
            // exposing the cached document would bypass the access result.
            // Ownership is content-free and independent of the rendered list:
            // a preview 403 removes its body, while its exact cached fallback
            // must stay suppressed even without a finalized receipt.
            const owners = { ...this.previewOwners };
            (this.documentPreviewOwnership || []).forEach(owner => { owners[owner.preview_id] = owner; });
            const handles = Object.values(owners).filter(owner => this.previewMatchesMessage(owner, message)).flatMap(preview => [
                this.previewReadyArtifacts[preview.preview_id],
                ...(this.previewUnavailableArtifacts[preview.preview_id] || []),
                (preview.denied || preview.pending) && preview.committed ? `A${preview.committed.artifact_id}:v${preview.committed.version}` : null,
            ]).filter(Boolean);
            const chat = String(this.session?.uuid || '');
            this.receiptsForMessage(message).forEach(receipt => {
                if (this.isDocumentArtifactReceipt(receipt) && ['ready', 'unavailable'].includes(this.artifactProjectionStates[artifactProjectionReceiptKey(chat, receipt)]?.state)) {
                    handles.push(`A${receipt.result_ref.id}:v${receipt.ui_payload?.counts?.version}`);
                }
            });
            return [...new Set(handles)];
        },
        messageReferenceId(message) {
            if (!message) return '';
            if (message.message_id) return String(message.message_id);
            if (message.client_message_id) return String(message.client_message_id);
            const senderType = message.sender === 'bot' ? 'assistant' : (message.sender || 'message');
            // Only explicitly marked optimistic messages receive a random
            // client id. Canonical legacy rows without a durable id or
            // timestamp use their immutable conversation position instead.
            if (message._is_optimistic === true) {
                let randomPart = '';
                try {
                    randomPart = globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
                        ? globalThis.crypto.randomUUID()
                        : '';
                } catch (e) { }
                if (!randomPart) {
                    randomPart = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
                }
                const clientId = `client-${senderType}-${randomPart}`;
                message.client_message_id = clientId;
                message.message_id = clientId;
                return clientId;
            }
            const absoluteIndex = Number.parseInt(String(message._chat_absolute_index), 10);
            const turnId = String(message.turn_id || '').trim();
            const serverTimestamp = String(message.server_timestamp || '').trim();
            const sender = String(
                message.sender_email
                || message.sender_username
                || message.sender_display_name
                || senderType
            ).trim();
            const text = String(message.text || message.content || '').slice(0, 160);
            const stablePosition = turnId || Number.isFinite(absoluteIndex)
                ? `${turnId}|${Number.isFinite(absoluteIndex) ? String(absoluteIndex) : ''}`
                : `${serverTimestamp}|${sender}|${text}`;
            const seed = [
                'canonical',
                senderType,
                stablePosition,
            ].join('|');
            let hash = 0;
            for (let i = 0; i < seed.length; i += 1) {
                hash = ((hash << 5) - hash) + seed.charCodeAt(i);
                hash |= 0;
            }
            const id = `msg-${senderType}-${Math.abs(hash).toString(36)}`;
            return id;
        },
        messageRenderKey(message) {
            // The client id is intentionally the vnode key when present. A
            // placeholder keeps that id after the server acknowledges it, so
            // canonical promotion does not remount its component state.
            // Do not derive a key from object identity: canonical syncs create
            // fresh payload objects for the same persisted messages.
            return String(message && message.client_message_id || '').trim()
                || this.messageReferenceId(message)
                || 'message-empty';
        },
        messageViewportId(message) {
            if (!message) return '';
            return String(message.client_message_id || '').trim()
                || this.messageReferenceId(message);
        },
        isDocumentArtifactReceipt(receipt) {
            return !!(
                receipt
                && receipt.tool_name === 'crear_editar_archivo'
                && receipt.result_ref
                && receipt.result_ref.type === 'document'
            );
        },
        isEmptySourceReadReceipt(receipt) {
            const counts = receipt
                && receipt.ui_payload
                && typeof receipt.ui_payload.counts === 'object'
                ? receipt.ui_payload.counts
                : {};
            return !!(
                receipt
                && receipt.tool_name === 'leer_fuente'
                && receipt.status === 'partial'
                && Number(counts.passages) === 0
            );
        },
        isPendingSourceReadReceipt(receipt) {
            return !!(
                receipt
                && receipt.tool_name === 'leer_fuente'
                && String(receipt.status || 'running') === 'running'
            );
        },
        isDocumentReviewReceipt(receipt) {
            return !!(receipt && receipt.tool_name === 'revisar_documento');
        },
        isArtifactReceiptWithResponse(receipt) {
            return this.isDocumentArtifactReceipt(receipt)
                && !!String(receipt.response_message_id || '').trim();
        },
        isOrphanedArtifactReceipt(receipt) {
            return this.isArtifactReceiptWithResponse(receipt)
                && receipt.response_message_exists === false;
        },
        isOrphanedReviewReceipt(receipt) {
            return this.isDocumentReviewReceipt(receipt)
                && receipt.response_message_exists === false;
        },
        isUnhostedTerminalReviewReceipt(receipt) {
            if (!this.isDocumentReviewReceipt(receipt)) return false;
            const terminalStatuses = new Set(['completed', 'partial', 'failed', 'cancelled']);
            if (!terminalStatuses.has(String(receipt.status || ''))) return false;
            if (this.assistantHostForReceipt(receipt)) return false;
            const triggerId = String(receipt.trigger_message_id || '').trim();
            // If the trigger is also outside this page, retain the historical
            // position. A loaded trigger with no usable response is the durable
            // terminal-recovery case and must keep a visible route to the review.
            return !!(triggerId && this.loadedMessageIds.has(triggerId));
        },
        isReceiptForAssistantMessage(receipt, message) {
            if (!message || message.sender !== 'bot') return false;
            if (this.isDocumentReviewReceipt(receipt)) {
                return this.assistantHostForReceipt(receipt) === message;
            }
            if (!this.isDocumentArtifactReceipt(receipt)) return false;
            const responseId = String(receipt.response_message_id || '').trim();
            return !!responseId && responseId === this.messageReferenceId(message);
        },
        isPendingArtifactReceipt(receipt) {
            if (!this.isDocumentArtifactReceipt(receipt)) return false;
            const messages = this.session && Array.isArray(this.session.data)
                ? this.session.data
                : [];
            const responseId = String(receipt.response_message_id || '').trim();
            if (responseId && messages.some(message => (
                message && message.sender === 'bot' && message.isLoading === true
                && (responseId === this.messageReferenceId(message)
                    || responseId === String(message.response_message_id || '').trim())
            ))) {
                return true;
            }
            if (!this.replying || !this.isOrphanedArtifactReceipt(receipt)) return false;
            const triggerId = String(receipt.trigger_message_id || '').trim();
            const lastUser = [...messages].reverse().find(message => message && message.sender === 'user');
            return !!(triggerId && lastUser && triggerId === this.messageReferenceId(lastUser));
        },
        isReceiptAttachedToUnloadedMessage(receipt) {
            if (!receipt || receipt.trigger_message_exists !== true) return false;
            const triggerId = String(receipt.trigger_message_id || '').trim();
            return !!triggerId && !this.loadedMessageIds.has(triggerId);
        },
        receiptsForMessage(message) {
            const receipts = this.visibleToolReceipts;
            const messageId = this.messageReferenceId(message);
            const turnId = message && message.turn_id ? String(message.turn_id) : '';
            return receipts.filter((receipt) => {
                if (!receipt || typeof receipt !== 'object') return false;
                if (this.isReceiptForAssistantMessage(receipt, message)) return true;
                if (message.sender !== 'user') return false;
                // Review results belong to the assistant response's research
                // timeline, never to the user message that triggered the run.
                if (this.isDocumentReviewReceipt(receipt)) return false;
                // Once a durable artifact has an assistant-response ID, keep the
                // card with that response rather than duplicating it after the
                // triggering user message.
                if (this.isArtifactReceiptWithResponse(receipt)) return false;
                const triggerId = String(receipt.trigger_message_id || '');
                if (receipt.trigger_message_exists === true) {
                    return !!messageId && triggerId === messageId;
                }
                if (receipt.trigger_message_exists === false) return false;
                if (messageId && triggerId === messageId) return true;
                return !!(turnId && String(receipt.turn_id || '') === turnId && message.sender === 'user');
            });
        },
        assistantHostForReceipt(receipt) {
            if (!receipt) return null;
            const messages = this.session && Array.isArray(this.session.data)
                ? this.session.data
                : [];
            const responseId = String(receipt.response_message_id || '').trim();
            if (responseId) {
                const response = messages.find(message => (
                    message && message.sender === 'bot'
                    && this.messageReferenceId(message) === responseId
                ));
                return response || null;
            }

            const turnId = String(receipt.turn_id || '').trim();
            if (turnId) {
                const response = messages.find(message => (
                    message && message.sender === 'bot'
                    && String(message.turn_id || '').trim() === turnId
                ));
                if (response) return response;
            }

            const triggerId = String(receipt.trigger_message_id || '').trim();
            const triggerIndex = messages.findIndex(message => (
                message && message.sender === 'user'
                && this.messageReferenceId(message) === triggerId
            ));
            if (triggerIndex < 0) return null;
            for (let index = triggerIndex + 1; index < messages.length; index += 1) {
                const candidate = messages[index];
                if (!candidate) continue;
                if (candidate.sender === 'user') break;
                if (candidate.sender === 'bot') return candidate;
            }
            return null;
        },
        researchHostForReceipt(receipt) {
            if (!receipt || receipt.tool_name !== 'leer_fuente') return null;
            return this.assistantHostForReceipt(receipt);
        },
        sourceReceiptsForMessage(message) {
            if (!message || message.sender !== 'bot') return [];
            return this.visibleToolReceipts.filter(receipt => (
                receipt && receipt.tool_name === 'leer_fuente'
                && this.researchHostForReceipt(receipt) === message
            ));
        },
        reviewReceiptsForMessage(message) {
            if (
                !message
                || message.sender !== 'bot'
                // A completed tool call can arrive before the assistant has
                // finished its response. Keep the result hidden for the whole
                // stream so it does not get ahead of the model's conclusion.
                // Terminal empty responses still render their durable review.
                || message.isLoading === true
            ) return [];
            return this.visibleToolReceipts.filter(receipt => (
                this.isDocumentReviewReceipt(receipt)
                && this.assistantHostForReceipt(receipt) === message
            ));
        },
        visibleReceiptsForMessage(message) {
            return this.receiptsForMessage(message).filter(receipt => !(this.previewsForMessage?.(message) || []).some(preview => previewOwnsReceipt(preview, receipt))).filter(receipt => !(
                (
                    message && message.sender === 'bot' && message.isLoading === true
                    && this.isDocumentArtifactReceipt(receipt)
                )
                // Source reads are rendered only as research-history entries
                // by BotMessage. This also suppresses receipts whose original
                // assistant response was replaced by an edited turn.
                || (receipt && receipt.tool_name === 'leer_fuente')
                || (
                    message && message.sender === 'bot'
                    && this.isDocumentReviewReceipt(receipt)
                )
            ));
        },
        markArtifactReceiptState(payload = {}) {
            const receipt = payload.receipt || payload;
            const chatUuid = String(payload.chatUuid || (this.session && this.session.uuid) || '');
            const projectionKey = artifactProjectionReceiptKey(chatUuid, receipt);
            if (!projectionKey || projectionKey === '|||') return;
            const state = String(payload.state || 'ready');
            try {
                this.$set(this.artifactProjectionStates, projectionKey, {
                    state,
                    requestKey: String(payload.requestKey || ''),
                });
            } catch (e) {
                this.artifactProjectionStates[projectionKey] = {
                    state,
                    requestKey: String(payload.requestKey || ''),
                };
            }
        },
        artifactProjectionReadyForMessage(message) {
            const chatUuid = String(this.session && this.session.uuid || '');
            return this.receiptsForMessage(message).some(receipt => (
                this.isDocumentArtifactReceipt(receipt)
                && ['ready', 'unavailable'].includes(
                    this.artifactProjectionStates[
                        artifactProjectionReceiptKey(chatUuid, receipt)
                    ]?.state
                )
            ));
        },
        hasDocumentArtifactForMessage(message) {
            if (!message || message.sender !== 'bot') return false;
            const chatUuid = String(this.session && this.session.uuid || '');
            return this.receiptsForMessage(message).some((receipt) => {
                if (!this.isDocumentArtifactReceipt(receipt)) return false;
                const state = this.artifactProjectionStates[
                    artifactProjectionReceiptKey(chatUuid, receipt)
                ]?.state;
                return state !== 'unavailable';
            });
        },
        startEditLastUser() {
            const userMessages = this.$refs.userMessages;
            if (Array.isArray(userMessages) && userMessages.length > 0) {
                const lastUserComp = userMessages[userMessages.length - 1];
                if (lastUserComp && typeof lastUserComp.startEdit === 'function') {
                    lastUserComp.startEdit();
                    // After toggling edit mode, scroll the message into view and focus the textarea
                    this.$nextTick(() => {
                        const el = lastUserComp.$el;
                        if (el && typeof el.scrollIntoView === 'function') {
                            try {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                            } catch (e) {
                                // Fallback for older browsers
                                el.scrollIntoView(true);
                            }
                        }
                        // Try to focus the textarea without breaking scroll animation
                        try {
                            const textarea = el ? el.querySelector('textarea') : null;
                            if (textarea && typeof textarea.focus === 'function') {
                                // preventScroll avoids jumping after smooth scroll
                                textarea.focus({ preventScroll: true });
                            }
                        } catch (e) { /* no-op */ }
                    });
                }
            }
        }
    },

    watch: {
        'session.uuid'() {
            // MessageUI is intentionally reused by ChatLayout. Projection
            // readiness belongs to one chat and must never leak to the next.
            this.artifactProjectionStates = {};
            this.previewReadyArtifacts = {};
            this.previewUnavailableArtifacts = {};
            this.previewOwners = {};
        },
    },

    /*html*/
    template: `
        <div class="py-3">
            <div>
                <div
                    :key="messageRenderKey(message)"
                    v-for="(message, index) in session.data"
                    :data-message-ref="messageReferenceId(message)"
                    :data-message-viewport-id="messageViewportId(message)"
                    :data-message-sender="message.sender || ''"
                    :data-chat-index="message._chat_absolute_index !== undefined && message._chat_absolute_index !== null ? String(message._chat_absolute_index) : null"
                >
                    <user-message 
                        v-if="message.sender == 'user'" 
                        :message="message"
                        :index="index"
                        :is-last-user="index === lastUserIndex"
                        :replying="replying"
                        :collaborative="collaborative"
                        :chat-uuid="session && session.uuid ? String(session.uuid) : ''"
                        ref="userMessages"
                        @editUserMessage="$emit('editUserMessage', $event)"
                        @reactToMessage="$emit('reactToMessage', $event)"
                        @replyToMessage="$emit('replyToMessage', $event)"
                        @navigateToMessage="$emit('navigateToMessage', $event)"
                        @runQueuedAiRequest="$emit('runQueuedAiRequest', $event)"
                    />
                    <system-message v-else-if="message.sender == 'system'" :message="message"/>
                    <bot-message 
                        v-else-if="message.sender == 'bot'" 
                        :message="message" 
                        :chat-uuid="session && session.uuid ? String(session.uuid) : ''"
                        :response-audio-key="messageViewportId(message)"
                        :messages-count="session.data.length"
                        :replying="replying"
                        :is_call_log="session.is_call_log"
                        :collaborative="collaborative"
                        :last-completed-run-id="session.last_completed_run_id || null"
                        :artifact-projection-ready="artifactProjectionReadyForMessage(message)"
                        :ready-artifact-handles="readyArtifactHandles(message)"
                        :has-document-artifact="hasDocumentArtifactForMessage(message)"
                        :has-document-output="hasDocumentOutputForMessage(message)"
                        :has-live-document-preview="hasLiveDocumentPreviewForMessage(message)"
                        :source-receipts="sourceReceiptsForMessage(message)"
                        @documentResponseReady="$emit('documentResponseReady', $event)"
                        @messageCompleted="$emit('messageCompleted', $event)"
                        @messageWaitingForContext="$emit('messageWaitingForContext', $event)"
                        @messageError="$emit('messageError', $event)"
                        @suggestedQuestionsReceived="$emit('suggestedQuestionsReceived', $event)"
                        @messageUpdated="$emit('messageUpdated', $event)"
                        @researchActivityAdded="$emit('researchActivityAdded', $event)"
                        @cancel="$emit('cancel', $event)"
                        @retryAfterCancel="$emit('retryAfterCancel', $event)"
                        @editLastUser="$emit('editLastUser', $event)"
                        @replyToMessage="$emit('replyToMessage', $event)"
                        @navigateToMessage="$emit('navigateToMessage', $event)"
                        @toolAction="$emit('toolAction', $event)"
                    >
                        <template #after-research>
                            <tool-receipt
                                v-for="receipt in reviewReceiptsForMessage(message)"
                                :key="receipt.tool_run_id"
                                :receipt="receipt"
                                :assistant-reply="true"
                                :chat-uuid="session && session.uuid ? String(session.uuid) : ''"
                                :active-review="activeReview"
                                @action="$emit('toolAction', $event)"
                                @reviewAction="$emit('reviewAction', $event)"
                                @artifact-state="markArtifactReceiptState"
                            />
                        </template>
                        <template #after-response>
                            <live-document-card
                                v-for="preview in previewsForMessage(message)"
                                :key="preview.operation_id || preview.preview_id"
                                :preview="preview"
                                :receipt="receiptForPreview(preview)"
                                :chat-uuid="String(session.uuid || '')"
                                @action="$emit('toolAction', $event)"
                                @committed-ready="previewCommittedReady"
                                @committed-unavailable="previewCommittedUnavailable"
                            />
                        </template>
                    </bot-message>
                    <tool-receipt
                        v-for="receipt in visibleReceiptsForMessage(message)"
                        :key="receipt.tool_run_id"
                        :receipt="receipt"
                        :assistant-reply="isReceiptForAssistantMessage(receipt, message)"
                        :chat-uuid="session && session.uuid ? String(session.uuid) : ''"
                        :active-review="activeReview"
                        @action="$emit('toolAction', $event)"
                        @reviewAction="$emit('reviewAction', $event)"
                        @artifact-state="markArtifactReceiptState"
                    />
                </div>
                <div v-if="standaloneReceipts.length" class="tool-receipts-standalone" aria-label="Actividades sin mensaje asociado">
                    <tool-receipt
                        v-for="receipt in standaloneReceipts"
                        :key="receipt.tool_run_id"
                        :receipt="receipt"
                        :chat-uuid="session && session.uuid ? String(session.uuid) : ''"
                        :active-review="activeReview"
                        @action="$emit('toolAction', $event)"
                        @reviewAction="$emit('reviewAction', $event)"
                        @artifact-state="markArtifactReceiptState"
                    />
                </div>
            </div>
        </div>`
}
